import type {
  QueryTransitionsV3Query,
  TransitionsV3Breakdowns,
  TransitionsV3FlowsItem,
  TransitionsV3RespData,
} from '@revelio/data-access';

import type { TransitionMetricValues } from '../../PageConfigProvider/types/metric';
import {
  type BreakdownData,
  type BreakdownSingleDataSet,
  DataType,
  type SingleDataPoint,
} from '../types';

type TransitionsData = BreakdownSingleDataSet;
type TransitionsBreakdownKey = keyof Omit<
  TransitionsV3Breakdowns,
  '__typename'
>;

type TransitionsV3Data = NonNullable<
  QueryTransitionsV3Query['transitions_v3']
>['entities'];

export const convertTransitionsV3Data = ({
  data,
  secondaryDimension,
  metric,
}: {
  data: TransitionsV3Data;
  secondaryDimension?: TransitionsBreakdownKey;
  metric: TransitionMetricValues;
}): TransitionsData => {
  if (secondaryDimension) {
    if (metric === 'transitions_inflows_and_outflows') {
      return convertToSingleInflowOutflowData({
        data,
        breakdown: secondaryDimension,
      });
    }
    return convertToBreakdownSegmentedData({
      data,
      breakdown: secondaryDimension,
      metric,
    });
  }

  if (metric === 'transitions_inflow' || metric === 'transitions_outflow') {
    return convertToSingleFlowBreakdownData({ data, metric });
  }

  return convertToInflowsAndOutflowsData(data);
};

/** INFLOW & OUTFLOW BREAKDOWN */
const convertToInflowsAndOutflowsData = (
  data: TransitionsV3Data
): TransitionsData => {
  const transitionData = data.map((entity) => {
    const id = `${entity.id}`;
    const dim = entity.short_name;
    const inflowsBreakdown = entity.metrics.flows_inflows.items
      .map(mapFlowToDataPoint)
      .filter((item) => item !== null);
    const outflowsBreakdown = entity.metrics.flows_outflows.items
      .map(mapFlowToDataPoint)
      .filter((item) => item !== null);

    return {
      dim,
      id,
      breakdown: [],
      inflowBreakdown: inflowsBreakdown,
      outflowBreakdown: outflowsBreakdown,
    };
  });

  return { type: DataType.BREAKDOWN_SINGLE, data: transitionData };
};

/** SINGLE FLOW BREAKDOWN */
type FlowMetricName = Extract<
  keyof TransitionsV3RespData,
  'flows_inflows' | 'flows_outflows'
>;
const convertToSingleFlowBreakdownData = ({
  data,
  metric,
}: {
  data: TransitionsV3Data;
  metric: Extract<
    TransitionMetricValues,
    'transitions_inflow' | 'transitions_outflow'
  >;
}): TransitionsData => {
  const flowMetricName: FlowMetricName = (() => {
    switch (metric) {
      case 'transitions_inflow':
        return 'flows_inflows';
      case 'transitions_outflow':
        return 'flows_outflows';
    }
  })();

  const transitionData: BreakdownSingleDataSet['data'] = data.map(
    (entity): BreakdownData<SingleDataPoint> => {
      const dim = entity.short_name;

      const breakdown: SingleDataPoint[] =
        entity.metrics?.[flowMetricName]?.items
          ?.filter((item) => item.id >= 0)
          ?.map(mapFlowToDataPoint)
          ?.filter((item) => item !== null) ?? [];

      return {
        id: `${entity.id}`,
        dim,
        breakdown,
      };
    }
  );

  return { type: DataType.BREAKDOWN_SINGLE, data: transitionData };
};

/** SEGMENTED BREAKDOWN */
const convertToBreakdownSegmentedData = ({
  data,
  breakdown,
  metric,
}: {
  data: TransitionsV3Data;
  breakdown: TransitionsBreakdownKey;
  metric: Extract<
    TransitionMetricValues,
    'transitions_inflow' | 'transitions_outflow'
  >;
}): TransitionsData => {
  const currentItems = getFlowItems({
    data,
    breakdownType: 'breakdowns_current',
    breakdown,
  });
  const flowItems = getFlowItems({
    data,
    breakdownType:
      metric === 'transitions_inflow'
        ? 'breakdowns_inflows'
        : 'breakdowns_outflows',
    breakdown,
  });

  const breakdownData: BreakdownData<SingleDataPoint>[] = [
    { dim: 'Current', breakdown: currentItems },
    {
      dim: metric === 'transitions_inflow' ? 'Inflow' : 'Outflow',
      breakdown: flowItems,
    },
  ];
  return { type: DataType.BREAKDOWN_SINGLE, data: breakdownData };
};

/** SINGLE INFLOW & OUTFLOW BREAKDOWN */
const convertToSingleInflowOutflowData = ({
  data,
  breakdown,
}: {
  data: TransitionsV3Data;
  breakdown: TransitionsBreakdownKey;
}): TransitionsData => {
  const inflowsBreakdown = getFlowItems({
    data,
    breakdownType: 'breakdowns_inflows',
    breakdown,
  });
  const outflowsBreakdown = getFlowItems({
    data,
    breakdownType: 'breakdowns_outflows',
    breakdown,
  });

  return {
    type: DataType.BREAKDOWN_SINGLE,
    data: [
      { dim: 'Inflow', breakdown: inflowsBreakdown },
      { dim: 'Outflow', breakdown: outflowsBreakdown },
    ],
  };
};

/** HELPER FUNCTIONS */
const EXCLUDED_FLOW_DIMS: ReadonlySet<string> = new Set(['unknown', 'empty']);
const mapFlowToDataPoint = (
  flow: TransitionsV3FlowsItem
): SingleDataPoint | null => {
  if (
    !flow.short_name ||
    EXCLUDED_FLOW_DIMS.has(flow.short_name.toLowerCase()) ||
    flow.id < 0
  ) {
    return null;
  }
  return {
    dim: flow.short_name,
    id: `${flow.id}`,
    value: flow.count,
    share: flow.share,
  };
};

const getFlowItems = ({
  data,
  breakdownType,
  breakdown,
}: {
  data: TransitionsV3Data;
  breakdownType: Extract<
    keyof TransitionsV3RespData,
    'breakdowns_inflows' | 'breakdowns_outflows' | 'breakdowns_current'
  >;
  breakdown: TransitionsBreakdownKey;
}) => {
  const items: Map<string, number> = new Map();

  data.forEach((entity) => {
    entity.metrics[breakdownType][breakdown].forEach((item) => {
      items.set(
        item.short_name,
        (items.get(item.short_name) ?? 0) + item.count
      );
    });
  });

  const itemsArray: SingleDataPoint[] = Array.from(items.entries()).map(
    ([dim, value]) => ({ dim: dim === 'other' ? 'Other' : dim, value })
  );

  const shouldSort = ['role', 'geography', 'rics'].includes(breakdown);
  const sortedItems = shouldSort
    ? itemsArray.sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
    : itemsArray;

  return sortedItems;
};
